import { useIonModal, useIonRouter } from '@ionic/react';
import { useCallback, useEffect, useState } from 'react';
import { useSelector } from 'react-redux';

import { Print, PrintProps } from '@pages/PrintBadge/Print';
import { getKioskEnabled } from '@state/kiosk';

type PrintBadgeModalProps = Omit<PrintProps, 'checkInCode' | 'shouldCheckIn' | 'dismiss'>;
type PrintBadgeShowProps = Omit<PrintProps, 'fullPage' | 'testPrint' | 'dismiss'>;

interface UsePrintBadgeModalArgs extends PrintBadgeModalProps {
  onDismiss?: () => void;
}

export const usePrintBadgeModal = ({ onDismiss = undefined, ...props }: UsePrintBadgeModalArgs) => {
  const router = useIonRouter();
  const [renderProps, setRenderProps] = useState<PrintBadgeShowProps | undefined>(undefined);
  const isKiosk = useSelector(getKioskEnabled);

  const [showRaw, hide] = useIonModal(Print, {
    ...props,
    ...(renderProps || {}),
    fullScreen: false,
    dismiss: () => {
      hide();
      onDismiss?.();
      router.goBack();
    },
  });

  const show = useCallback((showProps: PrintBadgeShowProps) => {
    setRenderProps(showProps);
  }, []);

  // Whenever the renderProps change, we need to show the modal
  useEffect(() => {
    // Ignore the first render when no props have been set
    if (!renderProps) return;
    // Kiosk mode may show a sponsor image at the bottom of the modal, so we should show it as
    // full screen in kiosk mode. Organizer prints don't show a sponsor image, so that doesn't
    // need to be a full screen modal.
    showRaw({ cssClass: isKiosk ? 'modal-full-screen' : '', backdropDismiss: false });
  }, [isKiosk, renderProps, showRaw]);

  // Disabling rule since we're mimicing the `useIonModal` hook
  // eslint-disable-next-line @eventmobi/no-array-return-in-hook
  return [show, hide];
};
